import React, { useState, useCallback, useEffect } from 'react';
import styled from 'styled-components';
import { useTranslation } from 'react-i18next';
import Autosuggest from 'react-autosuggest';
import debounce from 'lodash/debounce';

import pxInRem from 'shared/helpers/styled-components/remHelper';

const Wrapper = styled.div<{ value: string; isOpen: boolean }>`
  width: 100%;

  .react-autosuggest__container {
    position: relative;
  }

  & input {
    color: var(--text-default);
    padding: 0 20px;
    font-size: ${pxInRem(14)};
    background-color: var(--surface-background);
    outline: none;
    position: relative;
    border-radius: var(--border-radius-m);
    width: 100%;
    height: 40px;
    line-height: 40px;
    font-style: ${props => (props.value ? 'normal' : 'italic')};
    border: ${props =>
      props.isOpen
        ? `1px solid var(--surface-border-default)`
        : `1px solid var(--surface-border-light)`};

    &:placeholder {
      color: var(--text-light);
    }
  }

  & #react-autowhatever-1 {
    position: absolute;
    border-radius: var(--border-radius-m);
    width: 100%;
    max-height: 240px;
    margin-left: -1px;
    margin-top: 2px;
    left: 0;
    top: 39px;
    z-index: 2;
    background-color: var(--surface-background);
    font-size: ${pxInRem(14)};
    line-height: initial;
    overflow-y: auto;
    border: ${props =>
      props.isOpen ? `1px solid var(--surface-border-light)` : `0`};
  }
`;

const Suggestion = styled.div<{ disabled?: boolean; highlighted?: boolean }>`
  padding: 10px;
  white-space: nowrap;
  text-overflow: ellipsis;
  overflow: hidden;
  cursor: ${props => (props.disabled ? 'inherit' : 'pointer')};
  background-color: ${props =>
    props.highlighted ? 'var(--surface-primary)' : 'var(--surface-background)'};
`;

interface Item {
  value: string;
  label: string;
}

interface Props {
  className?: string;
  onChange: (value: string) => void;
  placeholder?: string;
  // https://github.com/moroshko/react-autosuggest#props
  inputProps?: Record<string, any>;
  items: Item[];
}

export default function TextAutoComplete({
  className,
  items,
  placeholder,
  inputProps,
  onChange
}: Props) {
  const { t } = useTranslation();

  const [value, setValue] = useState('');
  const [suggestions, setSuggestions] = useState<Item[]>([]);
  const [debouncedChange] = useState(() => debounce(onChange, 500));
  const [isInputFocused, setIsInputFocused] = useState(false);

  useEffect(() => {
    setSuggestions(
      items.filter(item =>
        item.label.toLowerCase().includes(value.toLowerCase())
      )
    );
  }, [items, value]);

  const shouldRenderSuggestions = value.length > 2;
  const onSuggestionsFetchRequested = useCallback(() => {}, []);
  const onSuggestionsClearRequested = useCallback(() => setSuggestions([]), []);

  const renderSuggestionsContainer = useCallback(
    ({ containerProps, children }) => {
      if (shouldRenderSuggestions && isInputFocused) {
        return (
          <div {...containerProps}>
            {suggestions.length ? (
              children
            ) : (
              <Suggestion disabled>{t('common.select.noResult')}</Suggestion>
            )}
          </div>
        );
      }

      return null;
    },
    [suggestions, shouldRenderSuggestions]
  );

  const renderSuggestion = useCallback(
    (suggestion: Item, { isHighlighted }) => (
      <Suggestion key={suggestion.value} highlighted={isHighlighted}>
        {suggestion.label}
      </Suggestion>
    ),
    []
  );

  const handleChange = useCallback(
    e => {
      setValue(e.target.value);
      debouncedChange(e.target.value);
    },
    [debouncedChange]
  );

  const handleSelect = useCallback(
    (_, { suggestionValue }) => {
      setValue(suggestionValue);
      debouncedChange(suggestionValue);
    },
    [debouncedChange]
  );

  const handleFocus = useCallback(() => setIsInputFocused(true), []);
  const handleBlur = useCallback(() => setIsInputFocused(false), []);

  return (
    <Wrapper
      className={className}
      value={value}
      isOpen={isInputFocused && shouldRenderSuggestions}
    >
      <Autosuggest
        suggestions={suggestions}
        onSuggestionsFetchRequested={onSuggestionsFetchRequested}
        onSuggestionsClearRequested={onSuggestionsClearRequested}
        getSuggestionValue={(suggestion: Item) => suggestion.value}
        renderSuggestionsContainer={renderSuggestionsContainer}
        renderSuggestion={renderSuggestion}
        shouldRenderSuggestions={() => shouldRenderSuggestions}
        onSuggestionSelected={handleSelect}
        focusInputOnSuggestionClick={false}
        inputProps={{
          ...(inputProps ?? {}),
          onChange: handleChange,
          onFocus: handleFocus,
          onBlur: handleBlur,
          placeholder,
          value
        }}
      />
    </Wrapper>
  );
}
